import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, CircularProgress, IconButton, InputAdornment, TextField } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import api from "services/api";
import { useLanguage } from "context/LanguageContext";
import { useSnackbar } from "context/SnackbarContext";
import SettingsSection from "./components/SettingsSection";
import SaveBar from "./components/SaveBar";

const GROUPS = [
  {
    title: "stAzure",
    fields: [
      ["azure_endpoint", "stEndpoint", false],
      ["azure_api_key", "stApiKey", true],
    ],
  },
  {
    title: "stContentUnderstanding",
    fields: [
      ["content_understanding_endpoint", "stEndpoint", false],
      ["content_understanding_api_key", "stApiKey", true],
    ],
  },
  {
    title: "stLlmProviders",
    fields: [
      ["openai_api_key", "OpenAI", true],
      ["anthropic_api_key", "Anthropic (Claude)", true],
      ["google_api_key", "Google (Gemini)", true],
    ],
  },
];

function SecretField({ label, value, onChange, secret }) {
  const { t } = useLanguage();
  const [shown, setShown] = useState(false);
  return (
    <TextField
      label={label}
      value={value}
      onChange={onChange}
      type={secret && !shown ? "password" : "text"}
      autoComplete="off"
      fullWidth
      InputProps={
        secret
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={shown ? t("stHide") : t("stShow")}
                    onClick={() => setShown((previous) => !previous)}
                    edge="end"
                    size="small"
                  >
                    {shown ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }
          : undefined
      }
    />
  );
}

SecretField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  secret: PropTypes.bool.isRequired,
};

function ApiKeysTab() {
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const [initial, setInitial] = useState(null);
  const [values, setValues] = useState(null);
  const [meta, setMeta] = useState({ companies: 0, differing: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/api/llm-api-keys/")
      .then((response) => {
        setInitial(response.data.keys);
        setValues(response.data.keys);
        setMeta({ companies: response.data.companies, differing: response.data.differing_fields });
      })
      .catch(() => showSnackbar(t("stLoadFailed"), "error"));
  }, []);

  if (!values) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const changed = Object.keys(values).filter((key) => values[key] !== initial[key]);
  // Values that differ between companies are sent too, so saving makes them the same everywhere.
  const toSend = [...new Set([...changed, ...meta.differing])];
  const fieldLabel = (key) => {
    const group = GROUPS.find((g) => g.fields.some(([name]) => name === key));
    const [, label] = group.fields.find(([name]) => name === key);
    return `${t(group.title)} – ${t(label)}`;
  };

  const save = async () => {
    setSaving(true);
    try {
      // Untouched keys are not sent: they stay as they are in every company.
      const payload = toSend.reduce((acc, key) => ({ ...acc, [key]: values[key] }), {});
      await api.put("/api/llm-api-keys/", payload);
      setInitial(values);
      setMeta((previous) => ({
        ...previous,
        differing: [],
      }));
      showSnackbar(t("stSaved"), "success");
    } catch (error) {
      showSnackbar(error.response?.data?.detail || t("stSaveFailed"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Alert severity="info" sx={{ mt: 3 }}>
        {t("stApiKeysHint").replace("{count}", meta.companies)}
      </Alert>
      {meta.differing.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t("stApiKeysDiffer").replace("{fields}", meta.differing.map(fieldLabel).join(", "))}
        </Alert>
      )}
      {GROUPS.map((group, index) => (
        <SettingsSection
          key={group.title}
          title={t(group.title)}
          last={index === GROUPS.length - 1}
        >
          {group.fields.map(([name, label, secret]) => (
            <SecretField
              key={name}
              label={t(label)}
              value={values[name]}
              secret={secret}
              onChange={(event) =>
                setValues((previous) => ({ ...previous, [name]: event.target.value }))
              }
            />
          ))}
        </SettingsSection>
      ))}
      <SaveBar dirty={toSend.length > 0} saving={saving} onSave={save} />
    </>
  );
}

export default ApiKeysTab;
